import clsx from "clsx";
import { useEffect, useRef, useState } from "react";
import {
    CalendarIcon,
    ChevronDownIcon,
    ChevronRightIcon,
} from "@heroicons/react/24/outline";

export interface PeriodOption {
    id: string;
    label: string;
    value?: string;
}

export interface PeriodCategory {
    id: string;
    label: string;
    options: PeriodOption[];
}

interface PeriodSelectorMenuProps {
    categories: PeriodCategory[];
    selectedCategoryId: string;
    selectedOptionId: string;
    onSelect: (categoryId: string, optionId: string) => void;
    displayLabel: string;
}

export function PeriodSelectorMenu({
    categories,
    selectedCategoryId,
    selectedOptionId,
    onSelect,
    displayLabel,
}: PeriodSelectorMenuProps) {
    const [isOpen, setIsOpen] = useState(false);
    const [hoveredCategoryId, setHoveredCategoryId] = useState<string | null>(
        null,
    );
    const menuRef = useRef<HTMLDivElement>(null);

    const toggleMenu = () => {
        if (isOpen) {
            setHoveredCategoryId(null);
        } else {
            setHoveredCategoryId(selectedCategoryId);
        }
        setIsOpen(!isOpen);
    };

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (
                menuRef.current &&
                !menuRef.current.contains(event.target as Node)
            ) {
                setIsOpen(false);
                setHoveredCategoryId(null);
            }
        };

        if (isOpen) {
            document.addEventListener("mousedown", handleClickOutside);
        }

        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [isOpen]);

    const handleCategoryHover = (categoryId: string) => {
        setHoveredCategoryId(categoryId);
    };

    const handleOptionClick = (categoryId: string, optionId: string) => {
        onSelect(categoryId, optionId);
        setIsOpen(false);
        setHoveredCategoryId(null);
    };

    return (
        <div className="relative" ref={menuRef}>
            <button
                onClick={toggleMenu}
                className={clsx(
                    "period-selector-button",
                    "flex w-fit cursor-pointer items-center justify-between gap-3 rounded-md border border-slate-600 bg-slate-800 px-3 py-2",
                    "body-s text-gray-200 transition-colors hover:border-slate-500 hover:bg-slate-700",
                )}
            >
                <span className="flex items-center gap-2">
                    <CalendarIcon className="size-4 text-gray-400" />
                    {displayLabel}
                </span>
                <ChevronDownIcon
                    className={clsx(
                        "size-3 text-gray-400 transition-transform duration-200",
                        isOpen && "rotate-180",
                    )}
                />
            </button>
            {isOpen && (
                <div
                    className={clsx(
                        "absolute right-0 top-full mt-1",
                        "z-50",
                        "flex items-start",
                    )}
                >
                    <div
                        className={clsx(
                            "w-[180px]",
                            "rounded-l-lg bg-slate-800 shadow-400",
                            "flex flex-col",
                        )}
                    >
                        {categories.map((category) => (
                            <button
                                key={category.id}
                                onMouseEnter={() =>
                                    handleCategoryHover(category.id)
                                }
                                className={clsx(
                                    "period-selector-category",
                                    "flex items-center justify-between px-4 py-3",
                                    "body-s text-left transition-colors",
                                    hoveredCategoryId === category.id
                                        ? "bg-slate-700 text-white"
                                        : "text-gray-300 hover:bg-slate-700/50",
                                )}
                            >
                                <span>{category.label}</span>
                                <ChevronRightIcon className="size-4" />
                            </button>
                        ))}
                    </div>

                    {hoveredCategoryId &&
                        (() => {
                            const category = categories.find(
                                (c) => c.id === hoveredCategoryId,
                            );
                            if (!category) return null;

                            return (
                                <div
                                    className={clsx(
                                        "w-[220px]",
                                        "rounded-r-lg bg-slate-800 shadow-400",
                                        "flex flex-col p-2",
                                    )}
                                >
                                    {category.options.map((option) => {
                                        const isSelected =
                                            selectedCategoryId ===
                                                category.id &&
                                            selectedOptionId === option.id;

                                        return (
                                            <button
                                                key={option.id}
                                                onClick={() =>
                                                    handleOptionClick(
                                                        category.id,
                                                        option.id,
                                                    )
                                                }
                                                className={clsx(
                                                    "period-selector-option",
                                                    "flex items-center gap-3 rounded px-3 py-2.5",
                                                    "body-s text-left transition-colors",
                                                    "hover:bg-slate-700",
                                                )}
                                            >
                                                <div
                                                    className={clsx(
                                                        "flex size-4 shrink-0 items-center justify-center rounded-full border-2",
                                                        isSelected
                                                            ? "border-blue-500"
                                                            : "border-gray-500",
                                                    )}
                                                >
                                                    {isSelected && (
                                                        <div className="size-2 rounded-full bg-blue-500" />
                                                    )}
                                                </div>
                                                <span
                                                    className={clsx(
                                                        "flex-1",
                                                        isSelected
                                                            ? "text-white"
                                                            : "text-gray-300",
                                                    )}
                                                >
                                                    {option.label}
                                                </span>
                                            </button>
                                        );
                                    })}
                                </div>
                            );
                        })()}
                </div>
            )}
        </div>
    );
}
